import { formatDecimal } from 'framework/utils';
import {
  SyberryTodayAllocationStatus,
  type SyberryTodayProjectAllocationSummary,
} from 'domain/people/syberryToday/types';
import { SyberryTodayApiAllocationStatus, type SyberryTodayApiCurrentWeekAllocationResponse } from './types';

const GUIDANCE_MESSAGE_BY_STATUS: Record<SyberryTodayAllocationStatus, string> = {
  [SyberryTodayAllocationStatus.ON_TRACK]: "You're on track with this week's plan",
  [SyberryTodayAllocationStatus.BEHIND]: "You're behind this week's plan",
  [SyberryTodayAllocationStatus.AHEAD]: "You're ahead of this week's plan",
  [SyberryTodayAllocationStatus.COMPLETED]: "You've completed planned allocation",
};

const STATUS_BY_API_STATUS: Record<SyberryTodayApiAllocationStatus, SyberryTodayAllocationStatus> = {
  [SyberryTodayApiAllocationStatus.ON_TRACK]: SyberryTodayAllocationStatus.ON_TRACK,
  [SyberryTodayApiAllocationStatus.BEHIND]: SyberryTodayAllocationStatus.BEHIND,
  [SyberryTodayApiAllocationStatus.AHEAD]: SyberryTodayAllocationStatus.AHEAD,
  [SyberryTodayApiAllocationStatus.COMPLETED]: SyberryTodayAllocationStatus.COMPLETED,
};

const formatHours = (hours: number) => `${formatDecimal(hours)}h`;

const buildAllocationLabel = (minimumPlannedHours: number, maximumPlannedHours: number) =>
  minimumPlannedHours === maximumPlannedHours
    ? `${formatHours(maximumPlannedHours)} planned this week`
    : `${formatHours(minimumPlannedHours)} - ${formatHours(maximumPlannedHours)} planned this week`;

const getMinAllocationMarkerPercent = (minimumPlannedHours: number, maximumPlannedHours: number) =>
  maximumPlannedHours > 0 ? (minimumPlannedHours / maximumPlannedHours) * 100 : 0;

export const mapSyberryTodayAllocationResponseToSummaries = ({
  projects,
}: SyberryTodayApiCurrentWeekAllocationResponse): SyberryTodayProjectAllocationSummary[] =>
  projects.map(project => {
    const status = STATUS_BY_API_STATUS[project.status];

    return {
      projectId: String(project.projectLegacyId),
      allocationLabel: buildAllocationLabel(project.minimumPlannedHours, project.maximumPlannedHours),
      plannedMinHours: project.minimumPlannedHours,
      plannedMaxHours: project.maximumPlannedHours,
      spentHours: project.spentHours,
      remainingMinHours: project.remainingMinimumHours,
      remainingMaxHours: project.remainingMaximumHours,
      exceededMaxHours: Math.max(0, project.spentHours - project.maximumPlannedHours),
      progressPercent: project.progressPercent,
      minAllocationMarkerPercent: getMinAllocationMarkerPercent(
        project.minimumPlannedHours,
        project.maximumPlannedHours,
      ),
      recommendedHoursToday: project.recommendedTodayHours,
      status,
      guidanceMessage: GUIDANCE_MESSAGE_BY_STATUS[status],
    };
  });
